import { ReactNode, RefObject } from 'react';
import { Header } from './Header';
import { StatusBar } from './StatusBar';
import { Sidebar, SidebarRef } from './Sidebar';
import type { AlertFilterState } from '@/lib/unitFilters';
import type { Unit } from '@/types';

interface DashboardLayoutProps {
  children: ReactNode;
  units: Unit[]; // Filtered units for status counts
  allUnits?: Unit[]; // Unfiltered units for dropdown unit counts
  selectedUnit: Unit | null;
  hoveredUnit?: Unit | null;
  onSelectUnit: (unit: Unit | null, tab?: 'solar' | 'charger' | 'hvac' | 'starlink') => void;
  onHoverUnit: (unit: Unit | null) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  statusFilters: Set<'online' | 'offline' | 'idle' | 'unknown'>;
  onStatusFiltersChange: (filters: Set<'online' | 'offline' | 'idle' | 'unknown'>) => void;
  showAlerts?: AlertFilterState;
  onShowAlertsChange?: (show: AlertFilterState) => void;
  onClearFilters: () => void;
  sidebarRef?: RefObject<SidebarRef | null>;
  isMobile?: boolean;
  isSidebarCollapsed?: boolean;
  onSidebarCollapsedChange?: (collapsed: boolean) => void;
  error?: string | null;
  onSearchOpen?: () => void;
  searchOpen?: boolean;
  onSearchOpenChange?: (open: boolean) => void;
  /** When true, content (map/dashboard) has finished loading - enables onboarding tooltips */
  contentLoaded?: boolean;
  /** Callback to reset viewport filtering */
  onResetViewportFilter?: () => void;
}

export function DashboardLayout({
  children,
  units,
  allUnits,
  selectedUnit,
  hoveredUnit,
  onSelectUnit,
  onHoverUnit,
  onRefresh,
  isRefreshing,
  searchQuery,
  onSearchChange,
  statusFilters,
  onStatusFiltersChange,
  showAlerts,
  onShowAlertsChange,
  onClearFilters,
  sidebarRef,
  isMobile = false,
  isSidebarCollapsed,
  onSidebarCollapsedChange,
  error,
  onSearchOpen,
  searchOpen = false,
  onSearchOpenChange,
  contentLoaded = false,
  onResetViewportFilter,
}: DashboardLayoutProps) {
  const onlineCount = units.filter((t) => t.status === 'online').length;
  const offlineCount = units.filter((t) => t.status === 'offline').length;
  const idleCount = units.filter((t) => t.status === 'idle').length;
  const warningCount = units.filter((t) => t.alerts && t.alerts > 0 && t.alertType === 'warning').length;
  const faultCount = units.filter((t) => t.alerts && t.alerts > 0 && t.alertType === 'fault').length;

  return (
    <div className="h-dvh flex flex-col bg-background">
      <Header
        onSearchClick={onSearchOpen}
        unitCount={units.length}
        totalUnits={allUnits?.length}
      />
      <StatusBar
        onlineCount={onlineCount}
        offlineCount={offlineCount}
        idleCount={idleCount}
        warningCount={warningCount}
        faultCount={faultCount}
        onClearFilters={onClearFilters}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        statusFilters={statusFilters}
        onStatusFiltersChange={onStatusFiltersChange}
        showAlerts={showAlerts}
        onShowAlertsChange={onShowAlertsChange}
        units={allUnits || units}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        searchOpen={searchOpen}
        onSearchOpenChange={onSearchOpenChange}
        contentLoaded={contentLoaded}
      />
      <div className="flex-1 relative overflow-hidden">
        {/* Map layer - Full screen underneath at z-0 */}
        <div className={`absolute inset-0 z-0 ${isMobile ? 'pb-16' : 'overflow-auto'}`}>
          {children}
        </div>

        {/* Sidebar layer - Floating on top at z-40 (desktop only) */}
        {!isMobile && (
          <div className="absolute left-0 top-0 bottom-0 z-40 pointer-events-none">
            <div className="pointer-events-auto h-full">
              <Sidebar
                ref={sidebarRef}
                units={units}
                totalUnits={allUnits?.length}
                selectedUnit={selectedUnit}
                hoveredUnit={hoveredUnit}
                onSelectUnit={onSelectUnit}
                onHoverUnit={onHoverUnit}
                isCollapsed={isSidebarCollapsed}
                onCollapsedChange={onSidebarCollapsedChange}
                error={error}
                onResetViewportFilter={onResetViewportFilter}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
